import { Header } from '../../../components/header';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Sidebar from '../../../components/Sidebar';
import TimesheetTabs from './components/TimesheetTabs';
import TimesheetCreation from './components/TimesheetCreation';
import TimesheetReports from './components/TimesheetReports';
import WorkedHours from './components/WorkedHours';
import { TimesheetTour } from '../../../components/tour/TimesheetTour';
import { useTimesheetTour } from '../../../components/tour/useTimesheetTour';
import { FirstTimeTourPrompt } from '../../../components/tour/FirstTimeTourPrompt';
import { subscribe as subscribeTour } from '../../../components/tour/tour-event-bus';
import { toast } from 'react-toastify';
import { getUserOnboarding } from '../../../../redux/auth/authSlice';
import { updateUserOnboarding } from '../../../features/onboarding/onboarding.service';
import { AppDispatch } from '../../../store';
import './timeSheetsPage.scss';
import GuideVideoModal from '../../../components/guide-video-modal.component';

/* Safely parse the `onBoardingSteps` field from the /useronboarding
   GET response — it arrives as a JSON string, not a nested object.
   Returns an empty object when the field is missing / malformed so
   callers can read flags off it without null checks. */
const parseOnboardingSteps = (raw: unknown): Record<string, unknown> => {
    if (typeof raw !== 'string' || !raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch { return {}; }
};

// These guide videos are hosted on the PROD blob only (same as the invoice tool's
// guide videos), so use the absolute prod URL — the env-based container points to the
// dev blob in development, where these files don't exist (404 → video won't play).
const videoUrlEn = 'https://invozioprod.blob.core.windows.net/files/timesheet_and_deductions_eng.mp4'
const videoUrlSv = 'https://invozioprod.blob.core.windows.net/files/timesheet_and_deduction_sv.mp4'

const videoCameraIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 10L20 7V17L15 14V10Z" stroke="#6750b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="3" y="7" width="12" height="10" rx="2" stroke="#6750b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const playArrowIcon = (
    <svg width="9" height="11" viewBox="0 0 9 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.5 1L8.5 5.5L0.5 10V1Z" fill="#6750b8" stroke="#6750b8" strokeWidth="0.5" strokeLinejoin="round"/>
    </svg>
);

function TimeSheetsPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('creating-timesheet');
    /* Per-user identity for the first-time tour prompt. Without this
       the prompt would never show for a newly-registered user who
       opens the app in a browser where a PREVIOUS user has already
       dismissed it — the localStorage flag is shared across accounts.
       Scoping the storage key by userId guarantees each account gets
       its own "first time" experience. */
    const userId = useSelector((state: any) => state?.auth?.user?.userId);

    /* Server-driven "should the Welcome to Timesheet modal show?" flag.
       Sourced from /useronboarding GET's `onBoardingSteps.IsTimesheetTourSkip`
       (arrives as a JSON string in the payload). The modal renders ONLY
       while the flag is explicitly false; once the user picks either
       Skip for Now or Start Tour, we PUT the flag to true and refresh
       the GET, so the modal stays hidden on every future visit until
       the flag is reset server-side. Contract:
         • null  — GET not yet resolved (skeleton state; don't show)
         • false — first-time user; SHOW the modal
         • true  — user has already dismissed; NEVER show */
    const userOnboardingData = useSelector((state: any) => state?.auth?.userOnboardingData);
    const isTimesheetTourSkip: boolean | null = useMemo(() => {
        if (!userOnboardingData) return null;
        const steps = parseOnboardingSteps(userOnboardingData?.onBoardingSteps);
        const flag = steps?.IsTimesheetTourSkip;
        return typeof flag === 'boolean' ? flag : null;
    }, [userOnboardingData]);
    /* Fetch the onboarding flags on every mount so the modal decision
       is always driven by the latest server state (not a stale Redux
       value from another page). Dispatched once on mount only. */
    useEffect(() => {
        dispatch(getUserOnboarding());
    }, [dispatch]);
    // Guided tour state — opens via the "Start Tour" button next to the
    // tutorial banner. The hook decides 2A (no clients yet) vs 2B (has
    // clients) at tour-start time using Redux state.
    const tour = useTimesheetTour();
    // Brief shimmer on first mount so the page feels alive
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    useEffect(() => {
        const timer = window.setTimeout(() => setIsInitialLoading(false), 1000);
        return () => window.clearTimeout(timer);
    }, []);

    /* When the user clicks Skip Tour inside the bubble, the hook
       emits `'tour:skipped'`. We:
         1. Show a friendly toast reassuring them the tour can be
            replayed from the Start Tour button.
         2. Flip `pulseStartTour` so the Start Tour CTA pulses for ~3s
            (two iterations of the 1.5s `tts-cta-pulse` keyframe),
            visually pointing the user at where to come back.
       The pulse class is auto-removed once the animation finishes. */
    const [pulseStartTour, setPulseStartTour] = useState(false);
    useEffect(() => {
        const unsub = subscribeTour('tour:skipped', () => {
            toast.info(t('TOUR_SKIPPED_TOAST') as string, { autoClose: 5000 });
            setPulseStartTour(true);
            window.setTimeout(() => setPulseStartTour(false), 3200);
        });
        return unsub;
    }, [t]);
     const lang = new URLSearchParams(window.location.search).get('lang') || 'en-us';
    const isSv = lang === 'en-us' ? false : true
    // Open the guide video in an in-app modal (same as the invoice tool) instead of a new tab.
    const [showGuideVideo, setShowGuideVideo] = useState(false);
    const handleButtonClick=() =>{
      setShowGuideVideo(true);
    }

    // Loaded timesheet entries from Redux - used to compute summary card values
    const timesheetData = useSelector((state: any) => state.timesheet?.timesheetData || []);

    const { totalHoursLabel, billableAmountLabel } = useMemo(() => {
        const totalMinutes = Math.round(
            (timesheetData as any[]).reduce((sum, item) => sum + (Number(item?.workingHour) || 0), 0) * 60
        );
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const billable = (timesheetData as any[]).reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);

        return {
            totalHoursLabel: `${hours}:${String(minutes).padStart(2, '0')}`,
            billableAmountLabel: `${Math.round(billable).toLocaleString('en-US')} SEK`,
        };
    }, [timesheetData]);

    const tabs = useMemo(() => [
        { key: 'creating-timesheet', label: t('CREATING_TIMESHEET') },
        { key: 'reports', label: t('REPORTS') },
        { key: 'worked-hours', label: t('WORKED_HOURS') }
    ], [t]);

    const renderContent = useMemo(() => {
        switch (activeTab) {
            case 'creating-timesheet':
                return <TimesheetCreation />;
            case 'reports':
                return <TimesheetReports />;
            case 'worked-hours':
                return <WorkedHours />;
            default:
                return <TimesheetCreation />;
        }
    }, [activeTab]);

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <Sidebar
                    activeItemId='timesheets-invoice'
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                <div className="dashboard-main-content">
                    <Header
                        setIsSidebarOpen={setIsSidebarOpen}
                    />
                    <div className='timesheet-page-container'>
                        <div className='create-timesheet-hero'>
                            {/* Start Tour CTA — anchored top-right of the hero card.
                                Floats above the title/subtitle so it's the first
                                thing the user notices when landing on the page. */}
                            <button
                                type="button"
                                className={`create-timesheet-hero__tour-cta${pulseStartTour ? ' create-timesheet-hero__tour-cta--pulse' : ''}`}
                                data-tour="start-tour-btn"
                                onClick={(e) => { e.stopPropagation(); tour.start(); }}
                            >
                                <svg className="create-timesheet-hero__tour-cta-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M10 8l6 4-6 4V8Z" fill="currentColor"/>
                                </svg>
                                <span>{t('START_TOUR') || 'Start Tour'}</span>
                            </button>
                            <h1 className='create-timesheet-hero__title'>{t('CREATE_TIMESHEET_TITLE') || 'Create Timesheet'}</h1>
                            <p className='create-timesheet-hero__subtitle'>{t('CREATE_TIMESHEET_SUBTITLE') || 'On this page, you can save and manage working hours for different clients. Whenever you want to create an invoice for a specific client in the future, simply click Invoice by Timesheet to generate an invoice using the working hours you have already recorded for that client.'}</p>
                          
                            <div
                                className='tutorial-banner'
                                onClick={handleButtonClick}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && handleButtonClick()}
                            >
                                <div className='tutorial-banner__left'>
                                    <div className='tutorial-banner__icon-wrapper'>
                                        {videoCameraIcon}
                                    </div>
                                    <span className='tutorial-banner__text--desktop'>{t('timeSheetBannerDesktop')}</span>
                                    <span className='tutorial-banner__text--mobile'>{t('timeSheetBannerMobile')}</span>
                                </div>
                                <div className='tutorial-banner__action'>
                                    <span>{t('timeSheetBannerWatch')}</span>
                                    {playArrowIcon}
                                </div>
                            </div>
                        </div>
                        <TimesheetTabs 
                            tabs={tabs}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                        />
                        <div className='timesheet-content'>
                            {renderContent}
                        </div>
                    </div>
                </div>
            </div>
            {/* Guided tour — mounted once at the page root so the
                reactour mask/portal can cover the whole layout. The
                tour itself only renders when `tour.isOpen` is true. */}
            <TimesheetTour api={tour} />
            {/* First-time onboarding prompt — appears once on a user's
                very first visit and asks whether they want to take the
                guided tour.

                Visibility is SERVER-driven: the /useronboarding GET's
                `onBoardingSteps.IsTimesheetTourSkip` flag controls
                whether the modal mounts. Only shown while the flag is
                explicitly `false`; the moment the user picks either
                Skip for Now or Start Tour, we PUT the flag to `true`
                and refresh the GET, so the modal stays permanently
                hidden across every future visit (any device, any
                browser). This replaces the old per-browser localStorage
                gate which lost state when the user cleared cookies or
                switched devices.

                Requires `userId` to be resolved so the auth context is
                ready when the PUT fires — otherwise the API call would
                run against an unauthenticated store slice. */}
            {userId && isTimesheetTourSkip === false && (
                <FirstTimeTourPrompt
                    /* storageKey is still passed because FirstTimeTourPrompt
                       internally uses it for its OWN visibility state,
                       BUT the parent-level `isTimesheetTourSkip === false`
                       guard is what actually decides mounting. Once the
                       server flag flips to true, the parent stops
                       rendering the component entirely. */
                    storageKey={`tour-prompt-seen:timesheet:user-${userId}`}
                    /* Animated Invozio tour-guide mascot (same character as
                       the Create-Invoice / Manage-Invoice tours) instead of
                       the plain 👋 emoji. */
                    mascot
                    mascotBubble={t('TOUR_TS_MASCOT_BUBBLE', { defaultValue: "Let's go, explore Timesheet!" })}
                    onStart={() => {
                        /* Fire the PUT + refresh in the background; do NOT
                           await, so the tour opens instantly with no
                           network latency between the user's click and
                           the first tour step. Even if the network fails
                           the tour still starts — the flag will simply
                           attempt to persist again on the next dismissal. */
                        updateUserOnboarding('isTimesheetTourSkip', true)
                            .then(() => dispatch(getUserOnboarding()))
                            .catch(() => { /* silent — non-blocking */ });
                        tour.start();
                    }}
                    /* Pulse starts when the user picks "Skip for Now"
                       (transition to the skip-confirmation view) and
                       continues until they click "Got it" on that
                       view. So the Start Tour CTA stays visibly
                       focused for the whole duration the user is
                       reading the "No problem!" message — no fixed
                       timer to outlive.

                       We ALSO fire the PUT here (not on Got it) so the
                       server flag flips immediately: a user who closes
                       the tab after clicking Skip for Now — before ever
                       reaching Got it — still won't see the modal on
                       their next visit. */
                    onSkip={() => {
                        updateUserOnboarding('isTimesheetTourSkip', true)
                            .then(() => dispatch(getUserOnboarding()))
                            .catch(() => { /* silent — non-blocking */ });
                        setPulseStartTour(true);
                    }}
                    onSkipDismiss={() => setPulseStartTour(false)}
                />
            )}

            {/* Guide video — opens in-app when the tutorial banner is clicked
                (same experience as the invoice tool's "watch a guide" video). */}
            <GuideVideoModal
                show={showGuideVideo}
                onClose={() => setShowGuideVideo(false)}
                videoUrl={isSv ? videoUrlSv : videoUrlEn}
                title={t('timeSheetGuideModalTitle', { defaultValue: 'How to create timesheets & invoices' })}
            />
        </div>
    );
}

export default TimeSheetsPage;
